import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { enqueueSnackbar } from "notistack";
import { createElement } from "react";
import { TbCopy, TbTrash } from "react-icons/tb";

import { hotelClinicStatusItems } from "@/constants/hotelClinicStatuses";
import { copyToClipboard } from "@/helpers/strings";
import useHotelClinicTasksBulkMutation from "@/providers/hooks/useHotelClinicTasksBulkMutation";
import {
    ClinicArrivalTasksBulkConfirmTypes,
    useClinicArrivalTasksGridActions,
} from "@/stores/use-clinic-arrival-tasks-grid";

import { HotelClinicTaskResolved } from "../../types";
import ClinicArrivalTasksActionsHeader from "./ClinicArrivalTasksActionsHeader";

function ClinicArrivalTasksActionsColumn({
    allRows,
    t,
}: {
    allRows: HotelClinicTaskResolved[];
    t: TFunction;
}): GridColDef<HotelClinicTaskResolved> {
    return {
        field: "actions",
        type: "actions",
        width: 60,
        cellClassName: "!px-2",
        pinnable: true,
        filterable: false,
        editable: false,
        disableColumnMenu: true,
        renderHeader: () => <ClinicArrivalTasksActionsHeader allRows={allRows} />,

        getActions: ({ row }) => {
            /* Same pattern as departure `TrackingActionsColumn`: hook inside `getActions` (MUI DataGrid). */
            // eslint-disable-next-line react-hooks/rules-of-hooks -- see HotelDepartureActionsColumn
            const { handleHotelClinicTasksBulkMutation, isPending } = useHotelClinicTasksBulkMutation();
            // eslint-disable-next-line react-hooks/rules-of-hooks -- see HotelDepartureActionsColumn
            const { updateBulkTasksConfirm } = useClinicArrivalTasksGridActions();

            const statusActions = hotelClinicStatusItems
                .filter((item) => row.status !== item.status)
                .map((item) => (
                    <GridActionsCellItem
                        icon={createElement(item.component(), { size: 14 })}
                        label={t(item.updateActionLabel)}
                        onClick={async () =>
                            handleHotelClinicTasksBulkMutation({
                                tasks: [{ taskId: row.id, updateData: { status: item.status } }],
                            })
                        }
                        color="primary"
                        showInMenu={true}
                        key={item.status}
                        disabled={isPending}
                    />
                ));

            const deleteAction = (
                <GridActionsCellItem
                    icon={<TbTrash size={14} />}
                    label={t("clinicArrival.deleteTaskRow")}
                    onClick={() =>
                        updateBulkTasksConfirm({
                            type: ClinicArrivalTasksBulkConfirmTypes.DELETE,
                            tasks: [{ taskId: row.id, updateData: { deleteTask: true } }],
                        })
                    }
                    color="error"
                    showInMenu={true}
                    key="delete-task"
                    disabled={isPending}
                />
            );

            const devActions = import.meta.env.DEV
                ? [
                      <GridActionsCellItem
                          icon={<TbCopy size={14} />}
                          label={t("clinicArrival.copyTaskId")}
                          onClick={async () => {
                              const copied = await copyToClipboard(row.id);
                              if (copied) {
                                  enqueueSnackbar(t("copied-to-clipboard"), { variant: "success" });
                              }
                          }}
                          color="primary"
                          showInMenu={true}
                          key="copy-task-id"
                      />,
                  ]
                : [];

            return [...statusActions, ...devActions, deleteAction];
        },
    };
}

export default ClinicArrivalTasksActionsColumn;
